import { HeartBank } from "../../../managers/HeartBank.ts";
import { analytics, performance, functions } from "../../../firebase.ts";
import { trace, PerformanceTrace } from "firebase/performance";
import { logEvent } from "firebase/analytics";
import { httpsCallable } from "firebase/functions";

//import Tesseract from "tesseract.js"; // does not work offline
import { Books } from "../../../managers/Books.ts";
import { Transcription2 } from "../../../models/Transcription.ts";
import { Transcriptions } from "../../../managers/Transcriptions.ts";
import { SwReader } from "../sw-reader/sw-reader.ts";
import { SwHeader } from "../../sw-header/sw-header.ts";
import { SwMain } from "../sw-main.ts";
import { SwFooter } from "../../sw-footer/sw-footer.ts";
import { SwShare } from "../sw-share/sw-share.ts";

import Hammer from "hammerjs";
import template from "./sw-page.mjs";
import Bedok from "../../../models/Bedok.json";
import Strings from "../../../models/Strings.json";
import { LANGUAGE, SW_AI, isApple, SW_COM } from "../../global.ts";
import { normalizeNumerals } from "../../../models/Number.ts";

import { InputCounter } from "flowbite";
import type { InputCounterOptions, InputCounterInterface } from "flowbite";
//import type { InstanceOptions } from "flowbite";
import POSTER from "../../../public/poster.png";

const LANGUAGE2 = "khmer";
const strings: any = Strings;
const bedok: any = Bedok;

type Response = {
    id: string | null;
    date: Date | null;
    transcript: string | null;
    text: string;
    hasText: boolean;
    audio: string | null;
};

export class SwPage extends HTMLElement {
    private heartbank = new HeartBank();
    private componentTrace: PerformanceTrace;
    private books = new Books();
    private transcriptions = new Transcriptions(); // manager
    private hammer = new Hammer(this, {
        touchAction: "pinch-zoom"
    }) as HammerManager;
    private timer: number = 0;

    private header = document.querySelector("sw-header") as SwHeader;
    private main = document.querySelector("sw-main") as SwMain;
    private footer = document.querySelector("sw-footer") as SwFooter;
    private pali = document.querySelector("sw-reader[lang=pali]") as SwReader;
    private khmer = document.querySelector("sw-reader[lang=khmer]") as SwReader;
    private shareComponent = document.querySelector("sw-share") as SwShare;

    private pageHeader: HTMLElement;
    private pager: HTMLElement;
    private page: HTMLInputElement;
    private total: HTMLElement;
    private increment: HTMLButtonElement;
    private decrement: HTMLButtonElement;

    private spinner: HTMLElement;
    private front: HTMLImageElement;
    private back: HTMLImageElement;
    private search: HTMLButtonElement;
    private share: HTMLButtonElement;

    private maxPage = 0;

    constructor() {
        super();
        this.componentTrace = trace(performance, "component");
        this.componentTrace.putAttribute("view", "sw-page");
        this.componentTrace.start();
        this.appendChild(template.content.cloneNode(true));

        this.spinner = this.querySelector("#spinner")!;
        this.front = this.querySelector("#sw-bedok-front")!;
        this.back = this.querySelector("#sw-bedok-back")!;
        this.search = this.querySelector("#sw-page-search")!;
        this.share = this.querySelector("#sw-page-share")!;

        this.pageHeader = this.querySelector("header")!;
        this.pager = this.querySelector("#sw-pager")!;
        this.page = this.querySelector("#page-number")!;
        this.total = this.querySelector("#page-total")!;
        this.increment = this.querySelector("#increment-button")!;
        this.decrement = this.querySelector("#decrement-button")!;
    }

    show() {
        this.classList.remove("hidden");
    }

    hide() {
        this.classList.add("hidden");
    }

    private renderFullScreen() {
        const paths = window.location.hash.split("/");
        if (paths[1] === "book") {
            if (localStorage.getItem("bars") === "false") {
                this.header.hide();
                //this.footer.hide(); // race condition
                this.footer.classList.add("hidden");
                this.classList.remove("py-[72px]");
            } else {
                this.header.show();
                //this.footer.show(); // race condition
                this.footer.classList.remove("hidden");
                this.classList.add("py-[72px]");
            }

            if (localStorage.getItem("pager") === "false") {
                this.pageHeader.classList.add("hidden");
            } else {
                this.pageHeader.classList.remove("hidden");
            }

            this.toggleButtons();
            this.togglePage();
        }
    }

    private togglePage() {
        if (
            localStorage.getItem("bars") === "false" &&
            localStorage.getItem("pager") === "false"
        ) {
            this.front.classList.remove("object-contain");
            this.front.classList.replace("w-full", "w-auto");
            this.front.classList.replace("h-full", "h-screen");
            this.back.classList.remove("object-contain");
            this.back.classList.replace("w-full", "w-auto");
            this.back.classList.replace("h-full", "h-screen");
        } else {
            this.front.classList.add("object-contain");
            this.front.classList.replace("w-auto", "w-full");
            this.front.classList.replace("h-screen", "h-full");
            this.back.classList.add("object-contain");
            this.back.classList.replace("w-auto", "w-full");
            this.back.classList.replace("h-screen", "h-full");
        }
    }

    connectedCallback() {
        this.renderFullScreen();

        this.hammer.on("tap", async (event) => {
            const paths = window.location.hash.split("/");
            const book = parseInt(paths[2]);
            const page = parseInt(paths[4]);

            //TODO: add page flip animation
            switch (event.target.id) {
                case "sw-bedok-front":
                    if (
                        page !== this.getTotal(book) ||
                        this.hasLastTwoPages(book)
                    ) {
                        this.front.classList.add("hidden");
                        this.back.classList.remove("hidden", "opacity-0");
                        this.books.update(book, "khmer");
                    }
                    this.heartbank.alertAdmin("page/flip/to-back");
                    break;
                case "sw-bedok-back":
                    this.front.classList.remove("hidden", "opacity-0");
                    this.back.classList.add("hidden");
                    this.books.update(book, "pali");
                    this.heartbank.alertAdmin("page/flip/to-front");
                    break;
            }
        });

        // mobile = 2-finger zooming and panning; desktop = long press or double click
        this.hammer.get("press").set({ time: 500, threshold: 9, pointers: 1 });
        this.hammer.on("press", this.openPage.bind(this));
        this.hammer.on("doubletap", this.openPage2.bind(this)); // deprecated

        this.hammer.get("swipe").set({ direction: Hammer.DIRECTION_ALL });
        this.hammer.on("swipeleft", () => this.pageTo(true));
        this.hammer.on("swiperight", () => this.pageTo(false));
        this.hammer.on("swipeup", this.toggleBars.bind(this));
        this.hammer.on("swipedown", this.togglePager.bind(this));

        this.page.addEventListener("keyup", this.userInput2.bind(this)); // keydown = character not yet detected // keypress is deprecated
        this.page.addEventListener("change", this.processInput.bind(this));

        // TODO: later fix: for mobile pinch zooming: 2-finger panning okay, but 1 finger does not work
        // window.addEventListener("resize", () => {
        //     this.hammer.set({
        //         touchAction:
        //             window.visualViewport?.scale !== 1 ? "auto" : "pinch-zoom"
        //     });

        //     if (window.visualViewport?.scale !== 1) {
        //         this.classList.remove("touch-zoom");
        //     } else {
        //         this.classList.add("touch-zoom");
        //     }
        // });

        // deprecated
        // for desktop wheel zooming
        // window.addEventListener("wheel", (event) => {
        //     if (event.ctrlKey) {
        //         if (this.isZoomed()) {
        //             this.hammer.set({ touchAction: "auto" });
        //             this.classList.remove(
        //                 "overflow-x-hidden",
        //                 "overflow-y-hidden"
        //             );
        //             this.header.classList.add("hidden");
        //             this.footer.classList.add("hidden");
        //             this.pager.classList.add("hidden");
        //             this.front.classList.remove("h-full");
        //             this.back.classList.remove("h-full");
        //             this.front.classList.add("h-auto");
        //             this.back.classList.add("h-auto");
        //             this.classList.remove(
        //                 "w-screen",
        //                 "h-[80vh]",
        //                 "md:h-[90vh]",
        //                 "lg:h-[85vh]"
        //             );
        //             this.classList.add("w-auto", "h-auto");
        //         } else {
        //             this.hammer.set({ touchAction: "pinch-zoom" });
        //             this.classList.add(
        //                 "overflow-x-hidden",
        //                 "overflow-y-hidden"
        //             );
        //             this.header.classList.remove("hidden");
        //             this.footer.classList.remove("hidden");
        //             this.pager.classList.remove("hidden");
        //             this.front.classList.add("h-full");
        //             this.back.classList.add("h-full");
        //             this.front.classList.remove("h-auto");
        //             this.back.classList.remove("h-auto");
        //             this.classList.add(
        //                 "w-screen",
        //                 "h-[80vh]",
        //                 "md:h-[90vh]",
        //                 "lg:h-[85vh]"
        //             );
        //             this.classList.remove("w-auto", "h-auto");
        //         }
        //     }
        // });

        // deprecated: weird BUG when light mode is on
        this.increment.addEventListener("click", (event: Event) =>
            this.pageTo(true)
        );
        this.decrement.addEventListener("click", (event: Event) =>
            this.pageTo(false)
        );

        this.search.addEventListener("click", () => {
            alert(strings.soon[LANGUAGE]);
        });

        //TODO: v2 put pali or khmer in url
        this.share.addEventListener("click", async () => {
            this.share.disabled = true;
            this.share.classList.add("opacity-50");

            const paths = window.location.hash.split("/");
            const book = parseInt(paths[2]);
            const page = parseInt(paths[4]);

            const basket = this.books.getBasket(book);
            const language = this.front.classList.contains("hidden")
                ? "khmer"
                : "pali";
            const transcript = await this.transcriptions.getTranscription(
                book,
                page,
                language
            );

            let files: File[] = [];
            if (this.heartbank.isSuperAdmin()) {
                const url = this.getPage(book, page, language);
                const response = await fetch(url, {
                    mode: "cors"
                });
                const blob = await response.blob();
                const file = new File(
                    [blob],
                    `bedok-ai-${book}-${page}-${language}.silicon.wat.jpeg`,
                    {
                        type: "image/jpeg"
                    }
                );
                files = [file];
            }

            const title = `${
                strings.baskets3[LANGUAGE][basket]
            }: ${book.toKhmer(LANGUAGE)}`;
            const text = transcript?.transcript
                ? `${strings.page[LANGUAGE]}: ${transcript.transcript}`
                : `${strings.cover[LANGUAGE]}`;
            const link = `${SW_COM}/book/${book}/page/${page}?language=${LANGUAGE}&version=${language}`; //window.location.href

            this.shareComponent.show(title, text, link, files);
            this.share.disabled = false;
            this.share.classList.remove("opacity-50");
        });

        this.componentTrace.incrementMetric("count", 1);
        this.componentTrace.stop();
    }

    private async openPage() {
        const paths = window.location.hash.split("/");
        const book = parseInt(paths[2]);
        const language = await this.books.get(book);
        const src = language === "khmer" ? this.back.src : this.front.src;
        if (isApple) window.open(src, "_blank");
        else window.location.href = src;
        this.heartbank.alertAdmin("page/long-press/open-page");
    }

    private async openPage2() {
        const paths = window.location.hash.split("/");
        const book = parseInt(paths[2]);
        const language = await this.books.get(book);
        const src = language === "khmer" ? this.front.src : this.back.src;
        window.open(src, "_blank");
        this.heartbank.alertAdmin("page/double-tap/open-page");
    }

    private toggleButtons() {
        // if (
        //     localStorage.getItem("bars") === "true" &&
        //     localStorage.getItem("pager") === "true"
        // ) {
        //     this.search.classList.remove("hidden");
        //     this.share.classList.remove("hidden");
        // } else {
        //     this.search.classList.add("hidden");
        //     this.share.classList.add("hidden");
        // }
    }

    private toggleBars() {
        if (localStorage.getItem("bars") === "false") {
            this.header.show();
            this.footer.show();
            this.classList.add("py-[72px]");
            localStorage.setItem("bars", "true");
            this.heartbank.alertAdmin("page/swipe-up/show-bars");
        } else {
            this.header.hide();
            this.footer.hide();
            this.classList.remove("py-[72px]");
            localStorage.setItem("bars", "false");
            this.heartbank.alertAdmin("page/swipe-up/hide-bars");
        }
        this.toggleButtons();
        this.togglePage();
    }

    private togglePager() {
        if (localStorage.getItem("pager") === "false") {
            this.pageHeader.classList.remove("hidden");
            localStorage.setItem("pager", "true");
            this.heartbank.alertAdmin("page/swipe-down/show-pager");
        } else {
            this.pageHeader.classList.add("hidden");
            localStorage.setItem("pager", "false");
            this.heartbank.alertAdmin("page/swipe-down/hide-pager");
        }
        this.toggleButtons();
        this.togglePage();
    }

    // for desktop
    // private isZoomed(): boolean {
    //     return (
    //         Math.round((window.innerWidth / window.outerWidth) * 100) !== 100
    //     );
    // }

    // type = tel // weird redirect bug !!!
    private userInput(event: KeyboardEvent) {
        window.clearInterval(this.timer);
        this.timer = window.setInterval(this.processInput.bind(this), 1500);
    }

    // type = number
    private userInput2(event: KeyboardEvent) {
        this.page.value = normalizeNumerals(
            this.page.value,
            this.maxPage
        ).toString();
        //console.log(this.page.value);
        if (event.key === "Enter") this.processInput();
    }

    // type = number
    private processInput() {
        const paths = window.location.hash.split("/");
        const book = parseInt(paths[2]);
        const page = parseInt(this.page.value);
        //window.location.hash = `/book/${book}/page/${isNaN(page) ? 1 : page}`;
        window.location.setHash(book, isNaN(page) ? 0 : page);
        logEvent(analytics, "page", { at: "input", book, page });
    }

    private getTotal(book: number): number {
        return Math.ceil(bedok[LANGUAGE2].books[book - 1] / 2) - 1;
    }

    private hasLastTwoPages(book: number): boolean {
        return bedok[LANGUAGE2].books[book - 1] % 2 === 0;
    }

    private imagePromise(img: HTMLImageElement): Promise<void> {
        return img.complete
            ? Promise.resolve()
            : new Promise<void>((resolve, reject) => {
                  img.addEventListener("load", () => resolve());
                  img.addEventListener("error", () =>
                      reject(`Failed to load: ${img.src}`)
                  );
              });
    }

    // same as goTo
    private getPage(book: number, page: number, language: string): string {
        const front = page * 2 + 1; // pali
        const back = front + 1; // khmer

        // 0 => 1, 2
        // 1 => 3, 4
        // 2 => 5, 6

        let repo;
        if (book <= 22) {
            repo = 1;
        } else if (book <= 44) {
            repo = 2;
        } else if (book <= 66) {
            repo = 3;
        } else if (book <= 88) {
            repo = 4;
        } else {
            repo = 5;
        }

        const pali = `https://raw.githubusercontent.com/SiliconWat/bedok${repo}.siliconwat.dev/main/khmer/book-${book}/page-${front}.jpg`;
        const khmer =
            page !== this.getTotal(book) || this.hasLastTwoPages(book)
                ? `https://raw.githubusercontent.com/SiliconWat/bedok${repo}.siliconwat.dev/main/khmer/book-${book}/page-${back}.jpg`
                : POSTER;

        return language === "pali" ? pali : khmer;
    }

    //async
    goTo(book: number, page: number) {
        const fetchPage = trace(performance, "fetchPage");
        fetchPage.start();

        if (navigator.onLine) this.pager.classList.add("hidden");
        this.front.classList.add("hidden", "opacity-0");
        this.back.classList.add("hidden", "opacity-0");
        if (navigator.onLine) this.spinner.classList.remove("hidden");

        const front = page * 2 + 1; // pali
        const back = front + 1; // khmer

        // 0 => 1, 2
        // 1 => 3, 4
        // 2 => 5, 6

        let repo;
        if (book <= 22) {
            repo = 1;
        } else if (book <= 44) {
            repo = 2;
        } else if (book <= 66) {
            repo = 3;
        } else if (book <= 88) {
            repo = 4;
        } else {
            repo = 5;
        }

        this.front.src = `https://raw.githubusercontent.com/SiliconWat/bedok${repo}.siliconwat.dev/main/khmer/book-${book}/page-${front}.jpg`;
        if (page !== this.getTotal(book) || this.hasLastTwoPages(book))
            this.back.src = `https://raw.githubusercontent.com/SiliconWat/bedok${repo}.siliconwat.dev/main/khmer/book-${book}/page-${back}.jpg`;

        this.front.style.filter =
            document.documentElement.classList.contains("dark") &&
            front !== 1 &&
            front !== bedok[LANGUAGE2].books[book - 1]
                ? "invert(1)"
                : "invert(0)";

        this.back.style.filter =
            document.documentElement.classList.contains("dark") &&
            (!this.hasLastTwoPages(book) ||
                (this.hasLastTwoPages(book) &&
                    back !== bedok[LANGUAGE2].books[book - 1]))
                ? "invert(1)"
                : "invert(0)";

        // weird bug: can't load image when changing to dark mode!! maybe has to do with html canvas...
        // BENEFIT: actual image that user can saved to phone!
        // const src = `https://raw.githubusercontent.com/SiliconWat/bedok${repo}.siliconwat.dev/main/khmer/book-${book}/page-${page}.jpg`;
        // this.img.src =
        //     document.documentElement.classList.contains("dark") &&
        //     page !== 1 &&
        //     page !== bedok[LANGUAGE2].books[book - 1]
        //         ? await this.invertImage(src)
        //         : src;

        fetchPage.putAttribute("front", this.front.src);
        fetchPage.putAttribute("back", this.back.src || "null");
        fetchPage.incrementMetric("count", 1);

        //await
        Promise.all([
            this.books.get(book),
            this.imagePromise(this.front),
            this.imagePromise(this.back)
        ])
            .then((items) => {
                if (items[0] === "khmer") {
                    if (front === bedok[LANGUAGE2].books[book - 1]) {
                        this.front.classList.remove("hidden", "opacity-0");
                    } else {
                        this.back.classList.remove("hidden", "opacity-0");
                    }
                } else if (items[0] === "pali") {
                    this.front.classList.remove("hidden", "opacity-0");
                } else {
                    const url = new URL(window.location.href);
                    const language = url.searchParams.get("version") || "khmer"; // default = khmer // good for page 0 bc not good cover art
                    if (language === "khmer")
                        this.back.classList.remove("hidden", "opacity-0");
                    else this.front.classList.remove("hidden", "opacity-0"); // default = pali bc need show correct page 0
                }
            })
            .finally(() => {
                this.render();
                this.pager.classList.remove("hidden");
                this.spinner.classList.add("hidden");
                fetchPage.stop();
            });

        if (front !== 1 && front !== bedok[LANGUAGE2].books[book - 1]) {
            //await
            this.getText(book, page, "pali", this.front.src)
                .then((response) => {
                    this.pali.render(
                        "pali",
                        response.id,
                        response.date,
                        response.transcript,
                        response.text,
                        response.hasText,
                        response.audio
                    );
                })
                .catch((error) => {
                    console.error(error.message);
                    this.pali.render(
                        "pali",
                        null,
                        null,
                        null,
                        strings.transcribe2[LANGUAGE],
                        false,
                        null
                    );
                    //alert(error.message);
                    this.transcriptions.remove(book, page);
                });
        } else {
            this.pali.render(
                "pali",
                null,
                null,
                null,
                strings.transcribe1[LANGUAGE],
                false,
                null
            );
        }

        if (
            (!this.hasLastTwoPages(book) &&
                front !== bedok[LANGUAGE2].books[book - 1]) ||
            (this.hasLastTwoPages(book) &&
                back !== bedok[LANGUAGE2].books[book - 1])
        ) {
            //await
            this.getText(book, page, "khmer", this.back.src)
                .then((response) => {
                    this.khmer.render(
                        "khmer",
                        response.id,
                        response.date,
                        response.transcript,
                        response.text,
                        response.hasText,
                        response.audio
                    );
                })
                .catch((error) => {
                    console.error(error.message);
                    this.khmer.render(
                        "khmer",
                        null,
                        null,
                        null,
                        strings.transcribe2[LANGUAGE],
                        false,
                        null
                    );
                    //alert(error.message);
                    this.transcriptions.remove(book, page);
                });
        } else {
            this.khmer.render(
                "khmer",
                null,
                null,
                null,
                strings.transcribe1[LANGUAGE],
                false,
                null
            );
        }

        // TODO: weird loading bug when opening reader
        //this.spinner.classList.add("hidden");
        //fetchPage.stop();
    }

    // TODO: later convert firestore collection documents as github files
    // order: cache, github file // no dexie bc sw cache
    private async getText2(
        book: number,
        page: number,
        language: string
    ): Promise<Response> {
        const github = await fetch(
            `https://raw.githubusercontent.com/SiliconWat/bedok.siliconwat.dev/main/book-${book}/page-${page}/${language}.json`
        );
        const transcription = await github.json();
        const text =
            LANGUAGE === "khmer"
                ? transcription.transcript
                : transcription.translation;
        return {
            id: null,
            date: transcription.editedDate,
            transcript: transcription.transcript,
            text,
            hasText: Boolean(text),
            audio: transcription.audio
        };
    }

    private setupRealtimeListener(id: string, language: string) {
        if (language === "khmer")
            this.transcriptions.setupRealtimeListener(
                "khmer",
                id,
                LANGUAGE,
                this.khmer.render.bind(this.khmer)
            );
        if (language === "pali")
            this.transcriptions.setupRealtimeListener(
                "pali",
                id,
                LANGUAGE,
                this.pali.render.bind(this.pali)
            );
    }

    // order: dexie//NO, firestore, cloud function, Tesseract
    private async getText(
        book: number,
        page: number,
        language: string,
        imageUrl: string
    ): Promise<Response> {
        // dexie
        // const transcription = await this.transcriptions.get(
        //     book,
        //     page,
        //     language
        // );
        // if (transcription) {
        //     this.setupRealtimeListener(transcription.id, language);
        //     const text =
        //         LANGUAGE === "khmer"
        //             ? transcription.transcript
        //             : transcription.translation;
        //     return {
        //         id: transcription.id,
        //         date: transcription.editedDate,
        //         transcript: transcription.transcript,
        //         text: text || strings.transcribe3[LANGUAGE]
        //     };
        // } else {
        // firestore
        const transcription = await this.transcriptions.getTranscription(
            book,
            page,
            language
        );
        if (transcription) {
            this.setupRealtimeListener(transcription.id, language);
            const text =
                LANGUAGE === "khmer"
                    ? transcription.transcript
                    : transcription.translation;
            return {
                id: transcription.id,
                date: transcription.editedDate,
                transcript: transcription.transcript,
                text: text || strings.transcribe3[LANGUAGE],
                hasText: Boolean(text),
                audio: transcription.audio
            };
        } else {
            // cloud function: deprecated // will never reach here if refactor
            const transcription = await this.recognizeTextFromURL(
                book,
                page,
                language,
                imageUrl
            );
            if (transcription) {
                this.setupRealtimeListener(transcription.id, language);
                const text =
                    LANGUAGE === "khmer"
                        ? transcription.transcript
                        : transcription.translation;
                return {
                    id: transcription.id,
                    date: transcription.editedDate,
                    transcript: transcription.transcript,
                    text: text || strings.transcribe3[LANGUAGE],
                    hasText: Boolean(text),
                    audio: transcription.audio
                };
            } else {
                return await this.recognizeTextFromURL2(imageUrl); // no english translation
            }
        }
        //}
    }

    // deprecated bc showing: "currenly transcribing"
    private async recognizeTextFromURL(
        book: number,
        page: number,
        language: string,
        imageUrl: string
    ): Promise<Transcription2 | null> {
        if (!navigator.onLine) {
            //alert(strings.offline[LANGUAGE]);
            return null;
        }

        try {
            const ocrText = httpsCallable<{
                book: number;
                page: number;
                language: string;
                imageUrl: string;
            }>(functions, "ocrText", { limitedUseAppCheckTokens: true });
            const result = await ocrText({
                book,
                page,
                language,
                imageUrl
            });

            // dexie
            const transcription = result.data as Transcription2;
            await this.transcriptions.update(
                transcription.id,
                transcription.editedDate,
                transcription.book,
                transcription.page,
                transcription.language,
                transcription.transcript || "",
                transcription.translation || ""
            );
            return transcription;
        } catch (error: any) {
            console.error(error.message);
            return null;
        }
    }

    // Tesseract: deprecated
    private async recognizeTextFromURL2(imageUrl: string): Promise<Response> {
        // const response = await fetch(imageUrl);
        // const blob = await response.blob();
        // const objectURL = URL.createObjectURL(blob);

        // const {
        //     data: { text }
        // } = await Tesseract.recognize(objectURL, "khm");
        // URL.revokeObjectURL(objectURL);
        const text = null;

        return {
            id: null,
            date: null,
            transcript: text || strings.transcribe3[LANGUAGE],
            text: text || strings.transcribe3[LANGUAGE], // no english translation,
            hasText: Boolean(text),
            audio: null
        };
    }

    private render() {
        const paths = window.location.hash.split("/");
        const book = parseInt(paths[2]);
        const page = parseInt(paths[4]);

        this.page.value = page.toString();
        //this.page.dataset.inputCounterMax = this.getTotal(book).toString();
        this.total.textContent =
            this.getTotal(book) + " " + strings.total[LANGUAGE];

        const options: InputCounterOptions = {
            minValue: 0,
            maxValue: this.getTotal(book), // null = infinite
            onIncrement: () => {
                this.pageTo(true);
            },
            onDecrement: () => {
                this.pageTo(false);
            }
        };

        // const counterInput: InputCounterInterface = new InputCounter(
        //     this.page,
        //     this.increment,
        //     this.decrement,
        //     options
        // );
        this.maxPage = this.getTotal(book);
    }

    // swiping can go to end and beginning of book; but flowbite pager cannot
    pageTo(direction: boolean) {
        const paths = window.location.hash.split("/");
        let book = parseInt(paths[2]);
        let page = parseInt(paths[4]);

        if (direction) {
            page = page === this.getTotal(book) ? 0 : page + 1;
        } else {
            page = page === 0 ? this.getTotal(book) : page - 1;
        }

        //window.location.hash = `/book/${book}/page/${page}`;
        window.location.setHash(book, page);
        logEvent(analytics, "page", { direction, book, page });
        this.heartbank.alertAdmin(`page/swipe/${direction ? "left" : "right"}`);
    }

    // deprecated
    pageTo2(direction: boolean) {
        const paths = window.location.hash.split("/");
        let book, page;

        if (paths[2] !== undefined && paths[4] !== undefined) {
            book = parseInt(paths[2]);
            page = parseInt(paths[4]);

            if (direction) {
                page = page === this.getTotal(book) ? 0 : page + 1;
            } else {
                page = page === 1 ? this.getTotal(book) : page - 1;
            }
        } else {
            book = paths[2] !== undefined ? parseInt(paths[2]) : 0;

            if (direction) {
                page = 0;
            } else {
                page = this.getTotal(book);
            }
        }

        //window.location.hash = `/book/${book}/page/${page}`;
        window.location.setHash(book, page);
        logEvent(analytics, "page", { direction, book, page });
    }

    // deprecated
    private async invertImage(src: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous"; // To handle cross-origin issues
            img.src = src;

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    // Set canvas dimensions to match the image
                    canvas.width = img.width;
                    canvas.height = img.height;

                    // Draw the image on the canvas
                    ctx.drawImage(img, 0, 0);

                    // Get image data
                    const imageData = ctx.getImageData(
                        0,
                        0,
                        canvas.width,
                        canvas.height
                    );
                    const data = imageData.data;

                    // Invert colors
                    for (let i = 0; i < data.length; i += 4) {
                        data[i] = 255 - data[i]; // Red
                        data[i + 1] = 255 - data[i + 1]; // Green
                        data[i + 2] = 255 - data[i + 2]; // Blue
                        // Alpha (data[i + 3]) remains the same
                    }

                    // Put the modified image data back onto the canvas
                    ctx.putImageData(imageData, 0, 0);

                    // Export the canvas as a Data URL
                    resolve(canvas.toDataURL());
                } else {
                    console.error("Failed to get canvas context");
                    reject();
                }
            };
        });
    }
}
